import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ActionMenu,
  Button,
  DeleteModal,
  Modal,
  Table,
} from "@telluscompany/design-system";
import { MoreVerticalIcon, PlusCircle, Trash2 } from "lucide-react";

import { useLocalization } from "@i18n";
import { useUpdateTableUserPreferences } from "@shared/hooks";
import { Gs3TablePaginator } from "@shared/components";
import { useUserContext } from "@shared/contexts";
import {
  EMPTY_PAGINATION_META,
  parsePageSize,
} from "@shared/constants/pagination.constants";
import { serializeToSearchParams } from "@shared/hooks";

import { ComplexCombobox } from "../../../../shared";
import {
  useAssignComplexAdminMutation,
  useComplexAdminsQuery,
  useRemoveComplexAdminMutation,
  type AdminComplexAdminAssignment,
} from "../../api/complexAdmins";
import { AssignComplexAdminForm } from "./AssignComplexAdminForm";

interface ComplexAdminsSectionProps {
  /** Owner escreve; ownerViewer só lê. */
  canWrite: boolean;
  /** Se false (ex.: complexAdmin puro), esconde a seção inteira. */
  canRead: boolean;
}

export const ComplexAdminsSection = ({
  canWrite,
  canRead,
}: ComplexAdminsSectionProps) => {
  const { translate } = useLocalization();
  const { user: me } = useUserContext();
  const updateTableUserPreferences = useUpdateTableUserPreferences(me?.userPreferences);
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get("page") ?? 1));
  const limit = parsePageSize(searchParams.get("limit"));
  const [complexFilter, setComplexFilter] = useState<string>("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [removeTarget, setRemoveTarget] =
    useState<AdminComplexAdminAssignment | null>(null);

  const { data, isFetching } = useComplexAdminsQuery(
    {
      page,
      limit,
      complexId: complexFilter || undefined,
    },
    { enabled: canRead },
  );

  const assignMutation = useAssignComplexAdminMutation();
  const removeMutation = useRemoveComplexAdminMutation();

  if (!canRead) return null;

  const handleAssign = (data: { userId: string; complexId: string }) => {
    assignMutation.mutate(data, {
      onSuccess: () => setAssignOpen(false),
    });
  };

  const handleRemove = () => {
    if (!removeTarget) return;
    removeMutation.mutate(
      { complexId: removeTarget.complexId, assignmentId: removeTarget.id },
      {
        onSuccess: () => setRemoveTarget(null),
        onError: () => setRemoveTarget(null),
      },
    );
  };

  const createActionMenu = (rowData: {
    rowData: AdminComplexAdminAssignment;
  }) => {
    if (!canWrite) return null;
    const item = rowData.rowData;
    return (
      <ActionMenu
        menu={[
          {
            actions: [
              {
                label: translate(
                  "admin.access.complexAdmins.button.remove",
                ),
                onClick: () => setRemoveTarget(item),
                icon: <Trash2 size={16} />,
                colorTitle: "red" as const,
              },
            ],
          },
        ]}
        trigger={<MoreVerticalIcon />}
      />
    );
  };

  return (
    <section className="flex flex-col gap-3 rounded-md border border-muted-foreground/20 p-4">
      <header className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">
            {translate("admin.access.complexAdmins.sectionTitle")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {translate("admin.access.complexAdmins.sectionDescription")}
          </p>
        </div>
      </header>

      <div className="w-full max-w-sm">
        <ComplexCombobox
          label={translate(
            "admin.access.complexAdmins.filter.complexLabel",
          )}
          placeholder={translate(
            "admin.access.complexAdmins.filter.allComplexes",
          )}
          clearable
          value={complexFilter}
          onChange={(value) => {
            setComplexFilter(value);
            setSearchParams((current) =>
              serializeToSearchParams({ page: 1 }, current),
            );
          }}
        />
      </div>

      <Table<AdminComplexAdminAssignment>
        userId={me?.id ?? ""}
        tableId="admin-complex-admins-table"
        usersPreferencesFromServer={me?.userPreferences}
        updateUsersPreferencesOnServer={updateTableUserPreferences}
        TableActionMenu={
          canWrite ? (createActionMenu as React.ComponentType) : undefined
        }
        TableButtons={
          canWrite ? (
            <Button
              size="small"
              variant="primary"
              onClick={() => setAssignOpen(true)}
              iconLeft={<PlusCircle />}
            >
              {translate("admin.access.complexAdmins.button.assign")}
            </Button>
          ) : undefined
        }
        TablePaginator={
          <Gs3TablePaginator
            meta={data?.meta ?? EMPTY_PAGINATION_META}
            useUrlState
            useSearchParamsHook={useSearchParams}
          />
        }
        draggable
        enableTableColumnsSelector
        filterButtonText={translate(
          "admin.access.complexAdmins.table.filterButtonText",
        )}
        handleClickRow={() => {}}
        tableColumns={[
          {
            key: "userName",
            label: translate("admin.access.complexAdmins.table.column.user"),
            minWidth: "150px",
            sortType: "text",
          },
          {
            key: "userEmail",
            label: translate("admin.access.complexAdmins.table.column.email"),
            minWidth: "200px",
            sortType: "text",
          },
          {
            key: "complexName",
            label: translate(
              "admin.access.complexAdmins.table.column.complex",
            ),
            minWidth: "150px",
            sortType: "text",
          },
          {
            key: "createdAt",
            label: translate(
              "admin.access.complexAdmins.table.column.createdAt",
            ),
            minWidth: "150px",
            sortType: "date",
          },
        ]}
        tableData={(data?.data ?? []).map(
          (a) =>
            ({
              ...a,
              createdAt: new Date(a.createdAt).toLocaleDateString(),
            }) as { [key: string]: unknown; color?: string },
        )}
      />

      {(data?.data?.length ?? 0) === 0 && !isFetching && (
        <p className="text-sm text-muted-foreground">
          {translate("admin.access.complexAdmins.table.empty")}
        </p>
      )}

      <Modal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        title={translate("admin.access.complexAdmins.modal.assignTitle")}
        content={
          <AssignComplexAdminForm
            onSubmit={handleAssign}
            onCancel={() => setAssignOpen(false)}
            isSubmitting={assignMutation.isPending}
          />
        }
      />

      <DeleteModal
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemove}
        isDeleting={removeMutation.isPending}
        itemDescription={
          removeTarget
            ? `${removeTarget.userName} — ${removeTarget.complexName}`
            : undefined
        }
        title={translate("admin.access.complexAdmins.modal.removeTitle")}
        message={translate("admin.access.complexAdmins.modal.removeMessage")}
        alertMessage={translate("common.deleteModalAlertMessage")}
        confirmButtonText={translate(
          "admin.access.complexAdmins.button.remove",
        )}
        cancelButtonText={translate("common.cancel")}
        deletingButtonText={translate("common.deleting")}
      />
    </section>
  );
};
